import { useEffect, useRef, useState } from "react";
import { useSandbox, useSandboxExec } from "@vorloop/api-client-react";
import { IconTerminal, IconChevronDown, IconX } from "../layout/Icons";
import { InteractiveTerminal } from "./InteractiveTerminal";

/**
 * Terminal dock. When a sandbox is running for the project, each Enter
 * fires a real `POST /api/projects/:id/sandbox/exec` against the Fly
 * machine, captures stdout/stderr/exit, and renders it.
 *
 * Without a sandbox, falls back to a tiny mock shell (help/ls/echo/etc)
 * so the UI never feels dead and the user is nudged to "Start sandbox"
 * in the TopBar.
 *
 * Interactive PTY (xterm.js + WebSocket-multiplexed stdio) is a
 * follow-up — chunk 3c. The route, image, and lifecycle work either way.
 */
interface Line {
  readonly id: number;
  readonly kind: "in" | "out" | "err";
  readonly text: string;
}

const PROMPT = "$ ";

const CANNED: Record<string, (args: string[]) => string> = {
  help: () => [
    "Available (mock) commands:",
    "  help            list commands",
    "  ls              fake directory listing",
    "  pwd             current path",
    "  whoami          current user",
    "  echo TEXT       repeat back TEXT",
    "  clear           clear the buffer",
    "  npm <cmd>       fake-runs an npm command",
    "  pnpm <cmd>      fake-runs a pnpm command",
    "",
    "Live shell lands when we connect the WebSocket TTY in step 3.",
  ].join("\n"),
  ls: () => "app  components  assets  package.json  app.config.ts  tsconfig.json",
  pwd: () => "/workspace",
  whoami: () => "vorloop",
  echo: (args) => args.join(" "),
  npm: (args) =>
    args[0] === "install" || args[0] === "i"
      ? "added 0 packages in 12ms\n(mock — real install lands when sandbox is wired)"
      : `npm ${args.join(" ")}: mock OK`,
  pnpm: (args) =>
    args[0] === "install" || args[0] === "i"
      ? "Packages: +0\nDone in 18ms\n(mock — real install lands when sandbox is wired)"
      : `pnpm ${args.join(" ")}: mock OK`,
};

export function TerminalDock({ projectId }: { projectId: string }) {
  const sandbox = useSandbox(projectId);
  const exec = useSandboxExec(projectId);
  const sandboxRunning =
    sandbox.data?.sandbox?.status === "running" ||
    sandbox.data?.sandbox?.status === "starting";

  const [open, setOpen] = useState(false);
  const [lines, setLines] = useState<Line[]>([
    {
      id: 0,
      kind: "out",
      text: "Vor terminal. Start a sandbox in the top bar to run real commands.",
    },
  ]);
  const [input, setInput] = useState("");
  const [history, setHistory] = useState<string[]>([]);
  const [historyIdx, setHistoryIdx] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const idRef = useRef(1);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [lines]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Welcome lines re-key when the sandbox attaches so the user knows
  // they've switched from mock → real exec.
  useEffect(() => {
    if (sandboxRunning) {
      pushLine(
        "out",
        "Connected to sandbox · " +
          (sandbox.data?.sandbox?.region ?? "iad") +
          " · /workspace",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sandboxRunning]);

  async function run(raw: string) {
    const cmd = raw.trim();
    if (!cmd) return;
    setHistory((h) => [...h.slice(-50), cmd]);
    setHistoryIdx(null);
    pushLine("in", PROMPT + cmd);
    if (cmd === "clear") {
      setLines([]);
      return;
    }
    // Real exec when sandbox is up.
    if (sandboxRunning) {
      setBusy(true);
      try {
        const res = await exec.mutateAsync({ command: cmd });
        if (res.stdout) pushLine("out", res.stdout.trimEnd());
        if (res.stderr) pushLine("err", res.stderr.trimEnd());
        if (res.exitCode !== 0) pushLine("err", `exit ${res.exitCode}`);
      } catch (err) {
        pushLine("err", (err as Error).message);
      } finally {
        setBusy(false);
      }
      return;
    }
    // Mock fallback while no sandbox is up.
    const [name, ...args] = cmd.split(/\s+/);
    const fn = name ? CANNED[name] : undefined;
    if (fn) {
      const out = fn(args);
      if (out) pushLine("out", out);
    } else {
      pushLine(
        "err",
        `${name}: command not found (start a sandbox from the top bar to run real commands)`,
      );
    }
  }

  function pushLine(kind: Line["kind"], text: string) {
    setLines((arr) => [...arr, { id: idRef.current++, kind, text }]);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      run(input);
      setInput("");
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (history.length === 0) return;
      const next = historyIdx === null ? history.length - 1 : Math.max(0, historyIdx - 1);
      setHistoryIdx(next);
      setInput(history[next] ?? "");
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (historyIdx === null) return;
      const next = historyIdx + 1;
      if (next >= history.length) {
        setHistoryIdx(null);
        setInput("");
      } else {
        setHistoryIdx(next);
        setInput(history[next] ?? "");
      }
    }
  }

  return (
    <div
      style={{
        borderTop: "1px solid var(--border-default)",
        background: "#0a0c10",
        color: "#d1d5db",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        maxHeight: open ? 280 : 32,
        transition: "max-height 120ms",
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Collapse terminal" : "Expand terminal"}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 12px",
          background: "transparent",
          border: "none",
          color: "var(--text-tertiary)",
          fontSize: 11,
          cursor: "pointer",
          letterSpacing: 0.4,
          textTransform: "uppercase",
          fontWeight: 600,
        }}
      >
        <span style={{ display: "inline-flex" }}>
          <IconTerminal size={12} />
        </span>
        <span>Terminal</span>
        <span style={{ flex: 1 }} />
        <span
          style={{
            color: "var(--text-tertiary)",
            transform: open ? "rotate(0deg)" : "rotate(180deg)",
            display: "inline-flex",
          }}
        >
          <IconChevronDown size={12} />
        </span>
      </button>
      {open && sandboxRunning && (
        <div style={{ flex: 1, minHeight: 0 }}>
          <InteractiveTerminal projectId={projectId} />
        </div>
      )}
      {open && !sandboxRunning && (
        <>
          <div
            ref={scrollRef}
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              padding: "8px 12px",
              lineHeight: 1.5,
            }}
          >
            {lines.map((l) => (
              <div
                key={l.id}
                style={{
                  whiteSpace: "pre-wrap",
                  color:
                    l.kind === "err"
                      ? "#fca5a5"
                      : l.kind === "in"
                      ? "#c7d2fe"
                      : "#d1d5db",
                }}
              >
                {l.text}
              </div>
            ))}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              padding: "4px 12px 8px",
              borderTop: "1px solid #1f2937",
            }}
          >
            <span style={{ color: "#9ca3af", fontFamily: "var(--font-mono)", fontSize: 12 }}>
              {PROMPT}
            </span>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              spellCheck={false}
              autoCapitalize="off"
              autoComplete="off"
              disabled={busy}
              placeholder={
                busy
                  ? "Running…"
                  : sandboxRunning
                  ? "ls, npm install, git status, …"
                  : "no sandbox — start one in the top bar (limited mock here)"
              }
              style={{
                flex: 1,
                background: "transparent",
                border: "none",
                outline: "none",
                color: "#d1d5db",
                fontFamily: "var(--font-mono)",
                fontSize: 12,
                marginLeft: 4,
                opacity: busy ? 0.5 : 1,
              }}
            />
            {input && (
              <button
                type="button"
                onClick={() => setInput("")}
                aria-label="Clear input"
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#6b7280",
                  cursor: "pointer",
                  display: "inline-flex",
                }}
              >
                <IconX size={10} />
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
